import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Model } from 'survey-core';
import * as SurveyCore from 'survey-core';
import { Survey } from 'survey-react-ui';
import * as Sentry from '@sentry/react';
import { PaymentIntent, Stripe, StripeEmbeddedCheckoutOptions, loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { FunctionFactory } from 'survey-core';

import { analytics, logEvent } from '@/firebase';
import { getBootcampBySlug, getBootcampPricing } from '@/services/bootcamp';
import { getBootcampState, formatEventDateRange, getBookeoSlotRange, isEarlyBirdActive } from '@/utils/bootcamp';
import type { Bootcamp, BootcampPricingData, ProductConfig } from '@/types/bootcamp';
import { initBookeoQuestion } from '@/components/survey';

import airstream from '@/assets/images/billboard/airstream.jpg';
import airstreamSm from '@/assets/images/billboard/airstream-sm.jpg';

import 'survey-core/defaultV2.min.css';
import '@/assets/css/custom-fds.css';
import { fordCSS, themeJSON } from '@/assets/survey/FordTheme';
import { prepareForSurvey, prepareSurveyOnQuestionAdded } from '@/helpers/surveyTemplatesAll';
import { initSurveyFord } from '@/helpers/surveyTemplatesFord';

import baseSurvey from './baseSurvey.json';

// Import custom ModuleCards renderer
import '@/assets/survey/surveysjs_renderers/ModuleCards';

import { Typography, Button } from '@ford/ford-ui-components';
import styles from './_index.module.css';
import closeIcon from '@/assets/images/icons/ford/close.svg';
import { Disclaimers } from '@/components/Disclaimers';

// Initialize the shared BookeoQuestion and Ford custom components (minorwaiver, etc.)
initBookeoQuestion();
initSurveyFord();

SurveyCore.setLicenseKey('NDBhNThlYzYtN2EwMy00ZTgxLWIyNGQtOGFkZWJkM2NlNjI3OzE9MjAyNS0wMS0wNA==');

let stripe: Stripe | null;
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PK);
stripePromise.then(ret => {
  stripe = ret;
});

// Email validation function
function validateEmail(this: any, [questionName]: string[]) {
  if (!questionName) {
    this.returnResult(true);
    return;
  }

  const email = this.survey.getValue(questionName);
  if (!email) {
    this.returnResult(true);
    return;
  }

  fetch(`https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}validateEmail-e2jtv773lq-uc.a.run.app`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
    .then(response => {
      response.json().then(res => {
        const { results } = res;
        let valid = true;

        if (results?.valid === false) valid = false;
        if (results?.is_disposable === true) valid = false;
        if (results?.delivery_confidence < 20) valid = false;
        if (results.did_you_mean) valid = false;

        if (!valid) {
          this.question.errors = [];
        }

        this.returnResult(valid);
      });
    })
    .catch(err => {
      console.error('Email validation error:', err);
      this.returnResult(true); // Allow on error
    });
}

FunctionFactory.Instance.register('validateEmail', validateEmail, true);

interface StripeMetadata {
  promotion_code?: string;
  discount_amount?: string;
}

// Loading component
const LoadingState = ({ message = 'Loading bootcamp details...' }: { message?: string }) => (
  <div className={styles.lightContent}>
    <div className={styles.contentSection}>
      <div className={styles.loadingSpinner}>
        <div className={styles.loadingSpinner__progress}></div>
      </div>
      <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
        {message}
      </Typography>
    </div>
  </div>
);

// Not found component
const NotFoundState = () => (
  <div className={styles.lightContent}>
    <div className={styles.contentSection}>
      <Typography displayStyle="title-medium" displayColor="text-onlight-strong">
        Bootcamp Not Found
      </Typography>
      <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
        The bootcamp you're looking for doesn't exist or is no longer available.
      </Typography>
    </div>
  </div>
);

// Registration closed component
const RegistrationClosedState = ({ bootcamp, state }: { bootcamp: Bootcamp; state: string }) => (
  <div className={styles.lightContent}>
    <div className={styles.contentSection}>
      <Typography displayStyle="title-medium" displayColor="text-onlight-strong">
        {state === 'completed' ? 'Event Completed' : 'Registration Not Open'}
      </Typography>
      <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
        {state === 'completed'
          ? `The ${bootcamp.name} bootcamp has concluded. Thank you for your interest!`
          : `Registration for the ${bootcamp.name} bootcamp is not yet open. Please check back later.`}
      </Typography>
    </div>
  </div>
);

// Upgrade mode error - missing attendeeID
const UpgradeMissingIdState = () => (
  <div className={styles.lightContent}>
    <div className={styles.contentSection}>
      <Typography displayStyle="title-medium" displayColor="text-onlight-strong">
        Invalid Upgrade Link
      </Typography>
      <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
        This upgrade link is incomplete. Please use the upgrade link from your confirmation email or check-in receipt.
      </Typography>
    </div>
  </div>
);

// Upgrade mode - no paid products available
const NoPaidProductsState = ({ bootcamp }: { bootcamp: Bootcamp }) => (
  <div className={styles.lightContent}>
    <div className={styles.contentSection}>
      <Typography displayStyle="title-medium" displayColor="text-onlight-strong">
        No Upgrade Options Available
      </Typography>
      <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
        There are currently no upgrade options available for the {bootcamp.name} bootcamp.
      </Typography>
    </div>
  </div>
);

const Registration = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isMounted = useRef(true);

  // Check for after mode (skip Bookeo scheduling)
  const isAfterMode = searchParams.get('mode') === 'after';

  // Check for upgrade mode (skip Bookeo, fetch attendee data, show only paid modules)
  const isUpgradeMode = searchParams.get('mode') === 'upgrade';
  const attendeeIdParam = searchParams.get('attendeeID');
  const sourceProductParam = searchParams.get('sourceProduct');

  const [bootcamp, setBootcamp] = useState<Bootcamp | null>(null);
  const [pricing, setPricing] = useState<BootcampPricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const [transactionID, setTransactionID] = useState<string>();

  // Upgrade mode state
  const [attendeeData, setAttendeeData] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address_group?: Record<string, unknown>;
    fordOwner?: boolean;
    fordVIN?: string;
    fordPassID?: string;
    is18?: boolean;
    amountPaid?: number;
  } | null>(null);
  const [isAttendeeLoading, setIsAttendeeLoading] = useState(false);
  const [attendeeFetchError, setAttendeeFetchError] = useState<string | null>(null);

  // Load bootcamp data
  useEffect(() => {
    const loadBootcamp = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        const bootcampData = await getBootcampBySlug(slug);
        if (!bootcampData) {
          setLoading(false);
          return;
        }

        setBootcamp(bootcampData);

        // Fetch pricing
        try {
          const pricingData = await getBootcampPricing(bootcampData.id);
          setPricing(pricingData);
        } catch (pricingError) {
          console.error('Failed to fetch pricing:', pricingError);
          // Continue without live pricing - will use fallback
        }
      } catch (error) {
        console.error('Failed to load bootcamp:', error);
        Sentry.captureException(error, {
          tags: { location: 'Registration.loadBootcamp', slug },
        });
      } finally {
        setLoading(false);
      }
    };

    loadBootcamp();
  }, [slug]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch attendee data for upgrade mode
  useEffect(() => {
    if (!isUpgradeMode || !attendeeIdParam) return;

    const fetchAttendee = async () => {
      setIsAttendeeLoading(true);
      setAttendeeFetchError(null);

      try {
        const response = await fetch(
          `https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}getAttendee-e2jtv773lq-uc.a.run.app`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attendeeID: attendeeIdParam }),
          },
        );

        if (!response.ok) {
          throw new Error('Attendee not found');
        }

        const data = await response.json();
        if (isMounted.current) {
          setAttendeeData({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            address_group: data.address_group,
            fordOwner: data.fordOwner,
            fordVIN: data.fordVIN,
            fordPassID: data.fordPassID,
            is18: data.is18,
            amountPaid: data.amountPaid,
          });
        }
      } catch (error) {
        console.error('Error fetching attendee:', error);
        if (isMounted.current) {
          setAttendeeFetchError('Could not load your information. Please try scanning the upgrade QR code again.');
        }
      } finally {
        if (isMounted.current) {
          setIsAttendeeLoading(false);
        }
      }
    };

    fetchAttendee();
  }, [isUpgradeMode, attendeeIdParam]);

  // Create reverse lookup map for product keys
  const productLookup = useMemo(() => {
    if (!bootcamp) return { byBookeoId: {}, byKey: {} };

    const byBookeoId: Record<string, string> = {};
    const byKey: Record<string, ProductConfig & { key: string }> = {};

    Object.entries(bootcamp.products).forEach(([key, product]) => {
      byBookeoId[product.bookeoProductId] = key;
      byKey[key] = { ...product, key };
    });

    return { byBookeoId, byKey };
  }, [bootcamp]);

  // Create survey model
  const survey = useMemo(() => {
    if (!bootcamp) return null;

    const surveyJson = JSON.parse(JSON.stringify(baseSurvey));
    surveyJson.title = `Ford Towing Bootcamp ${bootcamp.name} Registration`;

    // In after mode or upgrade mode, remove the schedule page (skip Bookeo booking)
    if (isAfterMode || isUpgradeMode) {
      surveyJson.pages = surveyJson.pages.filter((p: any) => p.name !== 'schedule');

      if (isUpgradeMode) {
        // Skip the primaryDriver page — user data is injected from existing attendee record
        surveyJson.pages = surveyJson.pages.filter((p: any) => p.name !== 'primaryDriver');

        // If upgrading from Towing 101, show the pre-survey page (waiver, emergency contact, etc.)
        // Towing 101 attendees skip these during initial registration, so they must complete them on upgrade
        if (sourceProductParam?.toLowerCase().includes('towing 101')) {
          const preSurveyPage = surveyJson.pages.find((p: any) => p.name === 'upgradePreSurvey');
          if (preSurveyPage) {
            delete preSurveyPage.visibleIf;
          }
        } else {
          // Non-Towing-101 upgraders already completed pre-survey — remove the page
          surveyJson.pages = surveyJson.pages.filter((p: any) => p.name !== 'upgradePreSurvey');
        }
      } else {
        // After-mode: remove upgradePreSurvey page (not relevant)
        surveyJson.pages = surveyJson.pages.filter((p: any) => p.name !== 'upgradePreSurvey');
      }
    } else {
      // Configure Bookeo question in JSON BEFORE creating the model
      // This ensures properties are available when the component is instantiated
      const slotRange = getBookeoSlotRange(bootcamp);
      const schedulePage = surveyJson.pages.find((p: any) => p.name === 'schedule');
      if (schedulePage) {
        const bookeoElement = schedulePage.elements.find((e: any) => e.name === 'bookeo_booking');
        if (bookeoElement) {
          bookeoElement.bookeoKey = bootcamp.bookeo.apiKey;
          bookeoElement.customFieldId = bootcamp.bookeo.customFieldId;
          bookeoElement.timeZone = bootcamp.location.timezone;
          bookeoElement.slotStartTime = slotRange.startTime;
          bookeoElement.slotEndTime = slotRange.endTime;
          bookeoElement.bootcampName = bootcamp.name;
        }
      }
    }

    // Find the source product's list price (not what they actually paid — ignores coupons/early-bird)
    const sourceProductListPrice = (() => {
      if (!isUpgradeMode || !sourceProductParam || !pricing) return 0;
      for (const [key, product] of Object.entries(bootcamp.products)) {
        const pn = product.name?.toLowerCase() || '';
        const spn = sourceProductParam.toLowerCase();
        if (pn === spn || pn.includes(spn) || spn.includes(pn)) {
          return pricing.prices[key]?.amount || 0;
        }
      }
      return 0;
    })();

    // Build module choices in JSON BEFORE creating the model
    const moduleSelectionPage = surveyJson.pages.find((p: any) => p.name === 'moduleSelection');
    if (moduleSelectionPage) {
      // Set the rvShowNote HTML from bootcamp content
      const rvShowNoteElement = moduleSelectionPage.elements.find((e: any) => e.name === 'rvShowNote');
      if (rvShowNoteElement) {
        rvShowNoteElement.html = bootcamp.content?.regPageShowNote || '';
      }

      const moduleElement = moduleSelectionPage.elements.find((e: any) => e.name === 'selectedModule');
      if (moduleElement) {
        // In upgrade mode, only show paid modules (filter out free ones)
        // Sort by order property (lower numbers first, undefined order goes last)
        const productEntries = (
          isUpgradeMode
            ? Object.entries(bootcamp.products).filter(([, product]) => {
                // Upgrade mode: only show paid modules
                if (product.isFree) return false;
                // If upgrading from Hands-On, only show Ultimate Towing Experience
                if (sourceProductParam?.toLowerCase().includes('hands-on trailer backup')) {
                  return product.name?.toLowerCase().includes('ultimate towing');
                }
                return true;
              })
            : Object.entries(bootcamp.products)
        ).sort(([, a], [, b]) => (a.order ?? Infinity) - (b.order ?? Infinity));

        moduleElement.choices = productEntries.map(([key, product]) => {
          const price = pricing?.prices[key];
          const isFree = product.isFree || false;
          let priceDisplay = 'FREE';
          let priceSuperscript = '1'; // Superscript 1 for FREE (on-site upgrade availability)
          let originalPriceDisplay = '';
          let upgradePriceDisplay = '';

          if (price && price.amount > 0) {
            priceDisplay = price.priceDisplay;
            priceSuperscript = '2'; // Superscript 2 for paid (guest costs)

            // Calculate upgrade pricing if in upgrade mode and source product had a price
            if (isUpgradeMode && sourceProductListPrice >= 0) {
              const upgradePrice = Math.max(0, price.amount - sourceProductListPrice);

              if (upgradePrice < price.amount) {
                originalPriceDisplay = priceDisplay; // e.g. "$265"
                priceDisplay = upgradePrice === 0 ? 'FREE' : `$${upgradePrice.toFixed(0)}`;
                upgradePriceDisplay = 'Upgrade Price';
              }
            }
          }

          return {
            value: product.bookeoProductId,
            text: product.annotatedName || product.name,
            description: product.description || '',
            duration: product.duration || '',
            priceDisplay: priceDisplay,
            priceSuperscript: priceSuperscript,
            isFree: isFree,
            includedItems: product.includedItems || [],
            additionalInfo: product.additionalInfo || '',
            originalPriceDisplay: originalPriceDisplay,
            upgradePriceDisplay: upgradePriceDisplay,
          };
        });
      }
    }

    const surveyModel = new Model(surveyJson);
    surveyModel.css = fordCSS;
    surveyModel.applyTheme(themeJSON);

    prepareForSurvey(surveyModel);
    prepareSurveyOnQuestionAdded(null, { survey: surveyModel });

    surveyModel.questionErrorLocation = 'bottom';
    surveyModel.pagePrevText = '< Previous';

    // Apply secondary styling to Previous button
    const applyPrevButtonStyle = () => {
      setTimeout(() => {
        const prevButtons = document.querySelectorAll('input[value="< Previous"]');
        prevButtons.forEach(button => {
          if (button instanceof HTMLElement) {
            button.classList.remove('fds-button-primary');
            button.classList.add('fds-button-secondary');
          }
        });
      }, 100);
    };

    // Scroll to registration header on page change
    const scrollToRegistrationHeader = () => {
      setTimeout(() => {
        const signupElement = document.getElementById('signup');
        if (signupElement) {
          signupElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    };

    surveyModel.onAfterRenderSurvey.add(applyPrevButtonStyle);
    surveyModel.onCurrentPageChanged.add(applyPrevButtonStyle);
    surveyModel.onCurrentPageChanged.add(scrollToRegistrationHeader);

    // Build dynamic price expression
    const priceTotalQuestion = surveyModel.getQuestionByName('priceTotal');
    if (priceTotalQuestion && pricing) {
      // Build an expression that maps bookeo IDs to prices
      const priceConditions = Object.entries(bootcamp.products)
        .map(([key, product]) => {
          const priceData = pricing.prices[key];
          const amount = priceData?.amount || 0;
          const upgradeAmount = isUpgradeMode ? Math.max(0, Math.round(amount - sourceProductListPrice)) : amount;
          return `iif({selectedModule} = '${product.bookeoProductId}', ${upgradeAmount}, `;
        })
        .join('');
      const closingParens = ')'.repeat(Object.keys(bootcamp.products).length);
      priceTotalQuestion.expression = priceConditions + '0' + closingParens;
    }

    return surveyModel;
  }, [bootcamp, pricing, isAfterMode, isUpgradeMode, sourceProductParam]);

  // Handle URL parameters for pre-selection
  useEffect(() => {
    if (!survey || !bootcamp) return;

    const moduleParam = searchParams.get('module');
    const bidParam = searchParams.get('bid');
    const storedModule = sessionStorage.getItem('selectedModule');
    const storedBid = sessionStorage.getItem('selectedBookeoId');

    // Set module from URL or sessionStorage
    const moduleKey = moduleParam || storedModule;
    if (moduleKey && bootcamp.products[moduleKey]) {
      const product = bootcamp.products[moduleKey];
      // In upgrade mode, only allow pre-selecting valid upgrade targets
      if (isUpgradeMode) {
        if (product.isFree) {
          // Can't pre-select free modules in upgrade
        } else if (sourceProductParam?.toLowerCase().includes('hands-on trailer backup') &&
            !product.name?.toLowerCase().includes('ultimate towing')) {
          // Hands-On can only upgrade to Ultimate
        } else {
          survey.setValue('selectedModule', product.bookeoProductId);
        }
      } else {
        survey.setValue('selectedModule', product.bookeoProductId);
      }
    }
    if (storedModule) sessionStorage.removeItem('selectedModule');

    // Set pre-selected slot
    const slotId = bidParam || storedBid;
    if (slotId) {
      survey.getQuestionByName('bookeo_booking')?.setPropertyValue('preSelectedSlotId', slotId);
    }
    if (storedBid) sessionStorage.removeItem('selectedBookeoId');
  }, [searchParams, survey, bootcamp, isUpgradeMode, sourceProductParam]);

  // Pre-populate form with attendee data in upgrade mode
  useEffect(() => {
    if (!survey || !isUpgradeMode || !attendeeData) return;

    // Set attendee fields (including fields needed by the webhook for cart creation)
    if (attendeeData.firstName) survey.setValue('firstName', attendeeData.firstName);
    if (attendeeData.lastName) survey.setValue('lastName', attendeeData.lastName);
    if (attendeeData.email) survey.setValue('email', attendeeData.email);
    if (attendeeData.phone) survey.setValue('phone', attendeeData.phone);
    if (attendeeData.address_group) survey.setValue('address_group', attendeeData.address_group);
    if (attendeeData.fordOwner != null) survey.setValue('fordOwner', attendeeData.fordOwner);
    if (attendeeData.fordVIN) survey.setValue('fordVIN', attendeeData.fordVIN);
    if (attendeeData.fordPassID) survey.setValue('fordPassID', attendeeData.fordPassID);
    if (attendeeData.is18 != null) survey.setValue('is18', attendeeData.is18);

    // Store attendeeID for cart tracking
    if (attendeeIdParam) survey.setValue('upgradeAttendeeID', attendeeIdParam);
  }, [survey, isUpgradeMode, attendeeData, attendeeIdParam]);

  // Add survey event handlers
  useEffect(() => {
    if (!survey || !bootcamp) return;

    // Clear values when panel becomes hidden
    survey.onPanelVisibleChanged.add((sender, options) => {
      if (!options.visible) {
        const questions = options.panel.questions;
        for (let i = 0; i < questions.length; i++) {
          sender.setValue(questions[i].name, undefined);
        }
      }
    });

    // Analytics on value change
    survey.onValueChanged.add((sender, options) => {
      if (options.name === 'selectedModule') {
        const moduleKey = productLookup.byBookeoId[options.value];
        const product = bootcamp.products[moduleKey];
        const priceData = pricing?.prices[moduleKey];

        logEvent(analytics, 'select_item', {
          items: [
            {
              item_id: options.value,
              item_name: product?.name || 'Unknown',
              price: priceData?.amount || 0,
            },
          ],
          item_list_name: 'Towing Bootcamp Products',
          item_list_id: `${bootcamp.slug}_products`,
          bootcamp_id: bootcamp.id,
          bootcamp_name: bootcamp.name,
        });
      }
    });

    // Handle survey completion
    survey.onComplete.add((sender, options) => {
      const selectedBookeoId = sender.getValue('selectedModule');
      const moduleKey = productLookup.byBookeoId[selectedBookeoId];
      const product = bootcamp.products[moduleKey];
      const priceData = pricing?.prices[moduleKey];

      sender.setValue('eventID', bootcamp.id);
      sender.setValue('bootcampId', bootcamp.id);
      sender.setValue('moduleName', product?.name || 'Unknown');
      sender.setValue('cartDate', new Date());

      // Build cart object - handle after mode differently
      const cart = {
        ...sender.data,
      } as Record<string, unknown>;

      if (isUpgradeMode) {
        // Set source for upgrade-mode carts (backend will skip Bookeo booking)
        cart.source = `${bootcamp.slug}-upgrade`;
        // Link to original registration for tracking
        if (attendeeIdParam) cart.attendeeID = attendeeIdParam;
        // No bookeo_booking data needed - backend will create default

        // Save pre-survey data to surveys collection if waiver was completed during upgrade
        if (attendeeIdParam && sender.getValue('signature')) {
          fetch(`https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}saveSurvey-e2jtv773lq-uc.a.run.app`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              survey: {
                surveyType: 'preTD',
                attendeeID: attendeeIdParam,
                eventID: bootcamp.id,
                waiverComplete: true,
                signature: sender.getValue('signature'),
                waiver_agree: sender.getValue('waiver_agree'),
                emergencyContactName: sender.getValue('emergencyContactName'),
                emergencyContactRelationship: sender.getValue('emergencyContactRelationship'),
                emergencyContactPhone: sender.getValue('emergencyContactPhone'),
                specialAccommodations: sender.getValue('specialAccommodations'),
                hasMinor: sender.getValue('hasMinor'),
                surveyDate: new Date(),
              },
            }),
          }).catch(err => console.error('Failed to save upgrade pre-survey:', err));
        }
      } else if (isAfterMode) {
        // Set source for after-mode carts (backend will skip Bookeo booking)
        cart.source = `${bootcamp.slug}-after`;
        // No bookeo_booking data needed - backend will create default
      } else {
        // Normal flow - include Bookeo booking data
        cart.bookeo_booking = {
          ...sender.getValue('bookeo_booking'),
          bookeoKey: sender.getQuestionByName('bookeo_booking')?.getPropertyValue('bookeoKey'),
          customFieldId: sender.getQuestionByName('bookeo_booking')?.getPropertyValue('customFieldId'),
          seats: sender.getQuestionByName('bookeo_booking')?.getPropertyValue('seats'),
          productId: sender.getQuestionByName('bookeo_booking')?.getPropertyValue('productId'),
          eventId: sender.getQuestionByName('bookeo_booking')?.getPropertyValue('eventId'),
        };
      }

      const price = sender.getValue('priceTotal');

      logEvent(analytics, 'begin_checkout', {
        currency: 'USD',
        value: price,
        items: [
          {
            item_id: selectedBookeoId,
            item_name: product?.name || 'Unknown',
            price: priceData?.amount || 0,
            quantity: 1,
          },
        ],
        bootcamp_id: bootcamp.id,
        bootcamp_name: bootcamp.name,
      });

      setShowCheckout(true);

      // Save cart
      fetch(`https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}saveCart-e2jtv773lq-uc.a.run.app`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: cart }),
      })
        .then(response => response.json())
        .then(data => {
          setTransactionID(data.cartID);
          options.showDataSavingSuccess();

          // Create payment intent
          fetch(`https://${import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX}createPaymentIntent-e2jtv773lq-uc.a.run.app`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cartID: data.cartID,
              data: sender.data,
              priceID: priceData?.priceId || product?.stripePriceId,
              bootcampId: bootcamp.id,
              source: cart.source, // Pass source for Stripe metadata tracking
              ...(isUpgradeMode && attendeeIdParam ? { attendeeID: attendeeIdParam } : {}),
            }),
          })
            .then(response => response.json())
            .then(data => {
              setClientSecret(data.clientSecret);
            });
        })
        .catch(err => {
          console.error(err);
          Sentry.captureException(err, {
            tags: {
              page: bootcamp.name,
              location: 'surveyOnComplete',
              type: 'Stripe Payment Intent Error',
            },
          });
        });
    });
  }, [survey, bootcamp, pricing, productLookup, isAfterMode, isUpgradeMode, attendeeIdParam]);

  // Stripe checkout options
  const stripeOptions: StripeEmbeddedCheckoutOptions = {
    clientSecret: clientSecret || '',
    onComplete: async () => {
      if (!bootcamp || !pricing) return;

      const selectedBookeoId = survey?.getValue('selectedModule');
      const moduleKey = productLookup.byBookeoId[selectedBookeoId];
      const product = bootcamp.products[moduleKey];
      const price = survey?.getValue('priceTotal');
      const priceData = pricing.prices[moduleKey];

      if (isMounted.current) {
        setShowCheckout(false);
      }

      // Send analytics
      const analyticsPromise = (async () => {
        let promoCode = null;
        let promoValue = 0;

        if (stripe && clientSecret) {
          try {
            const result = await stripe.retrievePaymentIntent(clientSecret);
            if (result.paymentIntent && isMounted.current) {
              const metadata = (result.paymentIntent as PaymentIntent & { metadata?: StripeMetadata }).metadata || {};
              promoCode = metadata.promotion_code || null;
              promoValue = metadata.discount_amount ? parseFloat(metadata.discount_amount) : 0;
            }
          } catch (error) {
            console.error('Error retrieving payment intent:', error);
          }
        }

        await logEvent(analytics, 'purchase', {
          transaction_id: transactionID,
          value: price,
          currency: 'USD',
          tax: 0,
          shipping: 0,
          items: [
            {
              item_id: selectedBookeoId,
              item_name: product?.name || 'Unknown',
              price: priceData?.amount || 0,
              quantity: 1,
            },
          ],
          bootcamp_id: bootcamp.id,
          bootcamp_name: bootcamp.name,
          ...(promoCode && {
            promotion_id: promoCode,
            promotion_name: promoCode,
            discount: promoValue,
          }),
        });

        await new Promise(resolve => setTimeout(resolve, 1000));
      })();

      try {
        await analyticsPromise;
        window.location.href = '/purchaseThanks';
      } catch (error) {
        console.error('Error in analytics:', error);
        window.location.href = '/purchaseThanks';
      }
    },
  };

  // Scroll to checkout when clientSecret is set
  useEffect(() => {
    const scrollingElement = document.scrollingElement || document.body;
    if (clientSecret) {
      scrollingElement.scrollTop = scrollingElement.scrollHeight;
      setTimeout(() => {
        scrollingElement.scrollTop = scrollingElement.scrollHeight;
      }, 3000);
    }
  }, [clientSecret]);

  const openDialog = () => {
    dialogRef.current?.showModal();
  };

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Not found state
  if (!bootcamp) {
    return <NotFoundState />;
  }

  // Check registration status
  const state = getBootcampState(bootcamp);
  if (state !== 'registration_open') {
    return <RegistrationClosedState bootcamp={bootcamp} state={state} />;
  }

  // Upgrade mode edge cases
  if (isUpgradeMode) {
    // Missing attendeeID param
    if (!attendeeIdParam) {
      return <UpgradeMissingIdState />;
    }

    // Still loading attendee data
    if (isAttendeeLoading) {
      return <LoadingState message="Loading your information..." />;
    }

    // Check if any paid products exist
    const hasPaidProducts = Object.values(bootcamp.products).some(p => !p.isFree);
    if (!hasPaidProducts) {
      return <NoPaidProductsState bootcamp={bootcamp} />;
    }
  }

  // Render registration page
  return (
    <>
      <div className={styles.billboard}>
        <picture className={styles.billboard_image_container}>
          <source media="(min-width: 640px)" srcSet={airstream} />
          <img src={airstreamSm} alt="Ford F-150 towing an Airstream trailer" />
        </picture>

        <div className={styles.billboard_content}>
          <div className={styles.billboard_content_inner}>
            <h1
              className={styles.billboard_heading}
              dangerouslySetInnerHTML={{
                __html: bootcamp.content.billboardHeading.replace(/\n/g, '<br />'),
              }}
            />

            <div className={styles.billboard_button}>
              <Button
                variant="filled"
                onClick={() => {
                  logEvent(analytics, 'select_content', {
                    content_type: 'signup_button',
                    content_id: 'signup_billboard',
                    bootcamp_id: bootcamp.id,
                    bootcamp_name: bootcamp.name,
                  });
                  document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Sign Up Now
              </Button>
            </div>

            <button
              className={styles.details_button}
              onClick={() => {
                logEvent(analytics, 'select_content', {
                  content_type: 'disclaimer',
                  content_id: 'towing_disclaimer',
                  bootcamp_id: bootcamp.id,
                  bootcamp_name: bootcamp.name,
                });
                openDialog();
              }}
            >
              Important Information
            </button>
          </div>
        </div>
      </div>

      <dialog ref={dialogRef} className={styles.modal}>
        <button className={styles.modal_close} onClick={() => dialogRef.current?.close()} aria-label="Close">
          <img src={closeIcon} alt="Close" />
        </button>
        <div className={styles.modal_content}>
          <p className={styles.modal_text}>
            Please keep in mind that towing capability varies based on cargo, vehicle configuration, accessories, and
            number and weight of passengers. Max tow often requires optional equipment at an extra charge. Doorjamb is
            the source of what an actual vehicle can tow.
          </p>
        </div>
      </dialog>

      <div className={styles.lightContent}>
        <div className={styles.contentSection}>
          <div className={styles.mainContent}>
            <div className={styles.section}>
              <div className={styles.sectionTitleWrapper}>
                <h3>
                  <Typography displayStyle="title-medium" displayColor="text-onlight-strong">
                    Event Details
                  </Typography>
                </h3>
              </div>
              <div>
                <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
                  <ul className={styles.list}>
                    <li className={styles.listItem}>
                      <strong>Dates Available:</strong>{' '}
                      {formatEventDateRange(bootcamp.dates.eventStart, bootcamp.dates.eventEnd, bootcamp)}
                    </li>
                    <li className={styles.listItem}>
                      <strong>Location:</strong> {bootcamp.location.venue}, {bootcamp.location.city},{' '}
                      {bootcamp.location.state}
                    </li>
                    <li className={styles.listItem}>
                      <strong>Address:</strong> {bootcamp.location.address}
                    </li>
                  </ul>
                  <p className={styles.weatherNotice}>
                    In the experience of severe or inclement weather, Ford Towing Bootcamp may determine that it is in
                    the best interest of safety to either delay or suspend an Experience.
                  </p>
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.contentSection}>
          <div id="signup"></div>
          {isUpgradeMode && attendeeFetchError && (
            <div style={{ padding: '16px', marginBottom: '16px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
              <Typography displayStyle="body-1-regular" displayColor="text-onlight-subtle">
                {attendeeFetchError}
              </Typography>
            </div>
          )}
          {!showCheckout && survey ? (
            <Survey model={survey} />
          ) : (
            <>
              {clientSecret ? (
                <EmbeddedCheckoutProvider options={stripeOptions} stripe={stripePromise}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              ) : (
                <div className={styles.loadingSpinner}>
                  <div className={styles.loadingSpinner__progress}></div>
                </div>
              )}
              <div className={styles.checkoutBackButton}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCheckout(false);
                    setClientSecret('');
                    // Reset survey to running state on the last page
                    if (survey) {
                      (survey as any).isCompleted = false;
                      survey.currentPageNo = survey.visiblePageCount - 1;
                    }
                  }}
                >
                  &lt; Previous
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Disclaimers />
    </>
  );
};

export default Registration;
